'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'next/navigation';
import useSWR from 'swr';
import { getAlgoliaSearchClient, getProductsIndexName, getUserToken, type AlgoliaProduct } from '@/lib/algolia';
import { buildAlgoliaFilters } from '@/lib/algolia/build-filters';
import { useAlgoliaKey } from '@/providers/algolia-key';
import { resolveReplicaSuffix, type AlgoliaSortValue } from '@/lib/algolia/sort';
import type { AlgoliaFacetCounts } from '@/lib/algolia/types';
import type { Locale } from '@/project.config';

export type { AlgoliaFacetCounts };

export type { AlgoliaSortValue } from '@/lib/algolia/sort';

interface UseAlgoliaProductsOptions {
  /** Search query string. Empty string is valid (returns the index's default top hits). */
  query?: string;
  /** Number of hits per page (default 5 — autocomplete-friendly; PLP passes 24). */
  hitsPerPage?: number;
  /** Algolia page number, 0-indexed. Default 0. */
  page?: number;
  /**
   * commercetools category key to scope the query to. Mapped to an Algolia
   * `categories.key:"<key>"` filter on the fly. Undefined → no category
   * filter, the hook returns hits matching the query across the whole index.
   */
  categoryKey?: string;
  /**
   * Active product-selection keys from the dealer's selected store. Mapped to
   * an Algolia `(productSelection.productSelection.key:"A" OR ...)` filter
   * so autocomplete + search results stay inside the dealer's catalog.
   * Always applied — never excluded by disjunctive sibling queries (this is
   * server-enforced visibility, not a user-facing facet).
   * Undefined / empty array → no scoping (records leak across selections).
   */
  productSelectionKeys?: string[];
  /**
   * Facet attributes to request counts for (e.g. `["brand"]`). Requires the
   * attribute to be `searchable(...)` in the index's `attributesForFaceting`.
   * Counts come back in the `facets` field of the return value.
   *
   * Each entry is treated as a **disjunctive** facet: when the same
   * attribute is refined via `refinements`, the hook issues a sibling
   * query with that refinement removed so the facet retains its full
   * option list after selection (the canonical "multi-select brand"
   * behaviour — selecting POWERTECH must NOT hide Brass Monkey from
   * the pill).
   */
  facets?: string[];
  /**
   * Active user refinements per facet attribute, e.g. `{ brand: ["NEXTECH", "POWERTECH"] }`.
   * Each non-empty array becomes an OR-joined filter clause; clauses across
   * different attributes are AND-joined.
   */
  refinements?: Record<string, string[]>;
  /**
   * Sort order applied to the result set. Default `'recommended'` uses the
   * primary (relevance) index; any other value picks the corresponding
   * Algolia sort replica. Requires the replica to exist on the locale's
   * index — if absent, Algolia returns an error and the hook flips
   * `isError`. v1 sd* replicas exist on AU only; NZ is pending.
   */
  sort?: AlgoliaSortValue;
  /**
   * When true, the request sets `clickAnalytics: true` so the response
   * carries a `queryID`. Required for callers that fire Algolia Insights
   * `clickedObjectIDsAfterSearch` / `convertedObjectIDsAfterSearch`
   * events — those events MUST reference the queryID Algolia assigned
   * to the search that produced the clicked hit. Default `false` keeps
   * surfaces that don't need tracking (e.g. layout-only hits) lean.
   */
  clickAnalytics?: boolean;
  /** Override the locale-derived index. Useful for storybook / debug. */
  indexName?: string;
  /**
   * Auto-fetch toggle. When false, the hook holds the previous result and
   * never starts a new SWR request — useful for surfaces that need a
   * non-empty query to make sense (header autocomplete). Default `true`
   * for the PLP (category pages still need data even with an empty query).
   */
  enabled?: boolean;
  /**
   * Pre-resolved Algolia result for SSR fallback. When the server-side
   * fetch produced a result for the same query/page/refinements the hook
   * is about to request, hand it here so SWR seeds the cache with it and
   * the first render shows hits instead of the skeleton. SWR still fires
   * a background revalidation on mount — the client request runs with
   * `clickAnalytics: true` + userToken so Insights events get a fresh
   * per-user queryID. `fallbackData` only applies to the FIRST cache key
   * the hook resolves; subsequent param changes (sort/page/filter) fetch
   * normally.
   */
  fallbackData?: {
    hits: AlgoliaProduct[];
    nbHits: number;
    nbPages: number;
    facets: AlgoliaFacetCounts;
  };
}

export interface UseAlgoliaProductsReturn {
  hits: AlgoliaProduct[];
  nbHits: number;
  nbPages: number;
  facets: AlgoliaFacetCounts;
  /**
   * Algolia-assigned id for the search that produced these hits. Only
   * populated when the request was made with `clickAnalytics: true`.
   * Pass it to `trackClick` / `trackConvert` from `@/lib/algolia` so
   * Insights events attribute back to the right search.
   */
  queryID?: string;
  /**
   * The resolved Algolia index name for this query (post sort-replica
   * resolution + override). Required by Insights events — pass alongside
   * `queryID` to `trackClick` / `trackView` / `trackConvert`.
   */
  indexName: string;
  isLoading: boolean;
  isError: boolean;
}

const EMPTY_FACETS: readonly string[] = Object.freeze([]);
const EMPTY_REFINEMENTS: Readonly<Record<string, string[]>> = Object.freeze({});

// EWM-Algolia v1 — client-side Algolia query keyed by query + index + page.
//
// Locale → index resolution happens automatically (`en-AU` → `*_products_sdau`).
// PR-2 added category/page/enabled. PR-3 added `productSelectionKeys` for
// server-enforced dealer-catalog scoping. PR-4 added `facets` + `refinements`
// with disjunctive-facet semantics: when you filter on brand, the brand pill
// still shows all brand options. Implementation: each refined facet triggers
// a sibling query in the same multi-request batch with its own refinement
// clause removed, so the facet's full counts come back. Category + product-
// selection clauses are always applied to every request.
const useAlgoliaProducts = ({
  query = '',
  hitsPerPage = 5,
  page = 0,
  categoryKey,
  productSelectionKeys,
  facets = EMPTY_FACETS as string[],
  refinements = EMPTY_REFINEMENTS,
  sort = 'recommended',
  clickAnalytics = false,
  indexName,
  enabled = true,
  fallbackData,
}: UseAlgoliaProductsOptions = {}): UseAlgoliaProductsReturn => {
  const params = useParams();
  const locale = (params?.locale as Locale | undefined) ?? 'en-AU';
  // EWM-253 — per-dealer secured key (productSelection baked in). The
  // client builds its Algolia client from this, never the raw key.
  const { securedApiKey } = useAlgoliaKey();
  // Caller-supplied `indexName` wins (storybook/debug). Otherwise pick the
  // sort replica suffix from the FE sort value.
  const replicaSuffix = resolveReplicaSuffix(sort);
  const resolvedIndex = indexName ?? getProductsIndexName(locale, replicaSuffix);

  const trimmedQuery = query.trim();

  // SWR's `fallbackData` is unkeyed — it's returned for ANY cache miss,
  // not just the swrKey it was authored for. If we kept feeding it after
  // mount, every sort/filter/page change (which produces a new swrKey)
  // would surface the stale SSR seed before the real client fetch
  // returned. So we seed exactly once per mount: after the first
  // effect run, clear the seed and let SWR drive everything from cache
  // + network. Each route navigation remounts this hook, so the next
  // PLP visit picks up the fresh SSR fallback.
  const seedActiveRef = useRef(!!fallbackData);
  useEffect(() => {
    seedActiveRef.current = false;
  }, []);
  // EWM-760: TRUE positive — this ref is mutated in an effect and reads across
  // renders (render-purity hazard). Converting to `useState` is not behavior-
  // preserving: the effect's `setState` forces an extra synchronous re-render
  // before the fetch resolves, flipping `seededFallback` to `undefined` for the
  // *same still-loading* key. This causes a spurious flash from seeded data to
  // a loading state. The ref's intentional non-re-rendering write is the only
  // way to avoid this; suppress per the windcave pattern (behavior cost of
  // fixing outweighs the hazard's benign outcome) — see
  // docs/superpowers/plans/ewm-760-disables.md.
  // eslint-disable-next-line react-hooks/refs -- EWM-760: see comment above
  const seededFallback = seedActiveRef.current ? fallbackData : undefined;

  // Which requested facets are currently being refined → they need a
  // sibling query each so the pill keeps its full option list.
  const disjunctiveFacets = useMemo(
    () => facets.filter((attr) => (refinements[attr]?.length ?? 0) > 0),
    [facets, refinements],
  );

  // SWR cache key derived from every filter-affecting input. Refinements +
  // productSelectionKeys flattened to deterministic strings so deep-equal
  // objects don't cache-miss on every render.
  const refinementsKey = useMemo(
    () =>
      Object.entries(refinements)
        .filter(([, v]) => v && v.length > 0)
        .map(([k, v]) => `${k}=${[...v].sort().join(',')}`)
        .sort()
        .join('|'),
    [refinements],
  );
  const facetsKey = useMemo(() => [...facets].sort().join(','), [facets]);
  const productSelectionKey = useMemo(
    () => (productSelectionKeys && productSelectionKeys.length > 0 ? [...productSelectionKeys].sort().join(',') : ''),
    [productSelectionKeys],
  );

  const shouldFetch = enabled && (trimmedQuery.length > 0 || !!categoryKey);
  const swrKey = shouldFetch
    ? ([
        '/algolia/search',
        resolvedIndex,
        trimmedQuery,
        hitsPerPage,
        page,
        facetsKey,
        refinementsKey,
        categoryKey ?? '',
        productSelectionKey,
      ] as const)
    : null;

  const { data, isLoading, error } = useSWR(
    swrKey,
    async () => {
      const client = getAlgoliaSearchClient(securedApiKey);
      const mainFilters = buildAlgoliaFilters({ categoryKey, productSelectionKeys, refinements });

      // Anonymous-tracking token from the `_at_token` cookie set by edge
      // middleware. Bound to the request so the response's queryID can
      // be linked back to events fired from this user's session.
      // Undefined during the brief window before middleware runs (e.g.
      // first request after a hard reload mid-session); Algolia tolerates
      // a missing userToken and assigns a queryID anyway.
      const userToken = clickAnalytics ? getUserToken() : undefined;

      // Main query — returns hits + counts for any non-refined facets
      // already correctly post-filter.
      const requests: Array<{
        indexName: string;
        query: string;
        hitsPerPage: number;
        page: number;
        filters?: string;
        facets?: string[];
        clickAnalytics?: boolean;
        userToken?: string;
      }> = [
        {
          indexName: resolvedIndex,
          query: trimmedQuery,
          hitsPerPage,
          page,
          ...(mainFilters ? { filters: mainFilters } : {}),
          ...(facets.length > 0 ? { facets: [...facets] } : {}),
          ...(clickAnalytics ? { clickAnalytics: true } : {}),
          ...(userToken ? { userToken } : {}),
        },
      ];

      // Sibling queries — one per refined facet. Each drops only its own
      // refinement clause; category + productSelection stay applied so the
      // facet counts respect dealer scoping. `hitsPerPage: 0` because we
      // only care about facet counts on these.
      for (const attr of disjunctiveFacets) {
        const siblingFilters = buildAlgoliaFilters({
          categoryKey,
          productSelectionKeys,
          refinements,
          excludeAttr: attr,
        });
        requests.push({
          indexName: resolvedIndex,
          query: trimmedQuery,
          hitsPerPage: 0,
          page: 0,
          ...(siblingFilters ? { filters: siblingFilters } : {}),
          facets: [attr],
        });
      }

      const res = await client.search<AlgoliaProduct>({ requests });
      const results = (res.results ?? []) as Array<{
        hits?: AlgoliaProduct[];
        nbHits?: number;
        nbPages?: number;
        facets?: AlgoliaFacetCounts;
        queryID?: string;
      }>;
      const main = results[0];

      // Merge facet counts: start with main response's facets, then
      // overwrite each refined facet with its sibling-query counts.
      const mergedFacets: AlgoliaFacetCounts = { ...(main?.facets ?? {}) };
      disjunctiveFacets.forEach((attr, i) => {
        const sibling = results[i + 1];
        if (sibling?.facets?.[attr]) mergedFacets[attr] = sibling.facets[attr];
      });

      return {
        hits: main?.hits ?? [],
        nbHits: main?.nbHits ?? 0,
        nbPages: main?.nbPages ?? 0,
        facets: mergedFacets,
        queryID: main?.queryID,
      };
    },
    {
      keepPreviousData: true,
      // SSR seed for the very first render only — see `seededFallback`
      // above for the why. queryID is intentionally undefined: server
      // requests don't carry `clickAnalytics: true`, so the seed has no
      // queryID; the post-mount client revalidation supplies a real one.
      fallbackData: seededFallback ? { ...seededFallback, queryID: undefined } : undefined,
    },
  );

  return {
    hits: data?.hits ?? [],
    nbHits: data?.nbHits ?? 0,
    nbPages: data?.nbPages ?? 0,
    facets: data?.facets ?? {},
    queryID: data?.queryID,
    indexName: resolvedIndex,
    isLoading,
    isError: !!error,
  };
};

export default useAlgoliaProducts;
